import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { useState } from 'react';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9njedm8mbnljin5a1rg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
        setFormState('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请检查您的网络连接后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 麻豆影视在线观看 | 品牌介绍·口碑·联系方式"
        description="了解麻豆影视在线观看平台：品牌故事、发展历程、用户口碑、联系方式。麻豆影视致力于为用户提供高清流畅的在线影视视频播放服务。"
        keywords="麻豆影视关于我们,麻豆影视品牌介绍,麻豆影视口碑,麻豆影视联系方式,在线影视平台"
        canonicalUrl="https://www.madouys.com/about"
      />
      <SchemaOrg type="WebPage" data={{ name: '关于我们 - 麻豆影视', url: 'https://www.madouys.com/about', isPartOf: { '@type': 'WebSite', name: '麻豆影视', url: 'https://www.madouys.com/' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.madouys.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.madouys.com/about' },
      ] }} />

      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-10 md:pb-14">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            关于麻豆影视
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mb-10 max-w-[700px]">
            麻豆影视在线观看平台，您的专属在线影视视频门户
          </p>

          {/* Brand Introduction */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">品牌介绍</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  麻豆影视成立于2020年，是一家专注于在线影视视频播放的互联网平台。我们致力于为广大影视爱好者提供丰富、高清、流畅的在线观影体验，让每一位用户都能轻松享受精彩的影视内容。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  经过多年的发展，麻豆影视在线观看平台已经积累了海量的影视资源库，涵盖动作片、喜剧片、爱情片、科幻片、恐怖片、动漫、纪录片、电视剧、综艺等各类内容。我们与国内外多家内容提供商建立了合作关系，确保资源丰富且更新及时。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  我们的使命是"让每个人都能轻松享受影视的乐趣"。秉持这一理念，麻豆影视始终坚持免费在线观看的模式，无需注册、无需下载、无需付费，即点即播，真正做到为用户着想。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20with%20large%20screen%20displaying%20movie%20posters%2C%20creative%20workspace%20with%20warm%20lighting%2C%20plants%20and%20wooden%20furniture%2C%20professional%20atmosphere%2C%20clean%20aesthetic&width=800&height=500&seq=about-brand-01&orientation=landscape"
                  alt="麻豆影视品牌形象"
                  className="w-full h-full object-cover"
                  title="麻豆影视在线观看 - 品牌形象"
                />
              </div>
            </div>
          </section>

          {/* Stats / Highlights */}
          <section className="mb-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-movie-2-line', value: '10,000+', label: '影视资源' },
                { icon: 'ri-user-heart-line', value: '500万+', label: '活跃用户' },
                { icon: 'ri-thumb-up-line', value: '98.5%', label: '用户好评率' },
                { icon: 'ri-timer-flash-line', value: '24h', label: '每日更新' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="text-center p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-11 h-11 md:w-12 md:h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '张先生',
                  role: '资深影迷',
                  content: '麻豆影视在线观看确实不错，资源更新很快，画质也很清晰。最满意的是不用下载App，打开网页就能看，非常方便。推荐给了很多朋友！',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '追剧达人',
                  content: '作为一个电视剧爱好者，麻豆影视的剧集更新速度让我非常满意。热门剧更新及时，而且播放流畅不卡顿。已经成为我的日常追剧首选平台。',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '大学生',
                  content: '学习之余喜欢看电影放松，麻豆影视的资源真的很全。从经典老片到最新大片，什么类型都有。免费观看的模式对学生党太友好了！',
                  rating: 5,
                },
              ].map((testimonial, i) => (
                <div
                  key={i}
                  className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{testimonial.name}</div>
                      <div className="text-xs text-foreground-500">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-600">service@madouys.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-800 mb-1">微信公众号</h4>
                      <p className="text-sm text-foreground-600">麻豆影视在线观看</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-qq-line"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-800 mb-1">QQ交流群</h4>
                      <p className="text-sm text-foreground-600">群号：88888888</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">在线留言</h3>
                  <form id="about-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      提交留言
                    </button>
                    {formState === 'success' && (
                      <p className="text-sm text-green-600 text-center">{formMessage}</p>
                    )}
                    {formState === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formMessage}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}